import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { api } from '../../services/api';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

// Paleta categórica validada (orden fijo — la identidad de cada tipo de
// novedad depende de esta posición, no de cuáles tipos aparezcan en el
// rango de fechas elegido). Ver skill de dataviz: nunca reasignar por
// frecuencia/rango, siempre por la misma clave.
const PALETTE = {
  blue: '#2a78d6',
  orange: '#eb6834',
  aqua: '#1baf7a',
  yellow: '#eda100',
  magenta: '#e87ba4',
  green: '#008300',
  violet: '#4a3aa7',
  red: '#e34948',
};

// Identidad fija por tipo de novedad conocido; cualquier tipo no listado
// aquí (incluido "Otro" y los que no quepan en el top 6) cae en OTHER_COLOR.
const TYPE_COLORS = {
  Permiso: PALETTE.blue,
  Incapacidad: PALETTE.orange,
  Licencia: PALETTE.aqua,
  Compensatorio: PALETTE.yellow,
  Calamidad: PALETTE.magenta,
  'Cita medica': PALETTE.green,
  'Trabajado en casa': PALETTE.violet,
};
const OTHER_COLOR = PALETTE.red;

// Tipos que cuentan como ausentismo: horas que el colaborador NO trabajó
// por permiso o incapacidad. Compensatorio queda fuera a propósito — ese
// tiempo se cruza contra horas extra ya trabajadas, no es ausentismo.
const ABSENTEEISM_TYPES = new Set(['Permiso', 'Incapacidad']);

const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function monthLabel(key) {
  const [y, m] = key.split('-');
  const idx = Number(m) - 1;
  return `${MONTH_LABELS[idx] ?? m} ${y}`;
}

function fmtHours(h) {
  return (Math.round((Number(h) || 0) * 10) / 10).toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

const chartMargin = { top: 8, right: 12, bottom: 8, left: 8 };

function CustomTooltip({ active, payload, label, suffix }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md px-3 py-2 text-xs">
      <div className="font-medium text-gray-800 mb-0.5">{label}</div>
      {payload.map((p, idx) => (
        <div key={idx} className="text-gray-600">
          {p.name ? `${p.name}: ` : ''}
          <span className="font-semibold text-gray-800">{p.value}{suffix || ''}</span>
        </div>
      ))}
    </div>
  );
}

export default function RhIndicadoresModule({ location }) {
  const today = useMemo(() => new Date(), []);
  const startOfYear = useMemo(() => new Date(today.getFullYear(), 0, 1), [today]);

  const [filters, setFilters] = useState({ from: toYMD(startOfYear), to: toYMD(today) });
  const [applied, setApplied] = useState({ from: toYMD(startOfYear), to: toYMD(today) });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Filtro por tipo de novedad — a diferencia del rango de fechas, se
  // aplica al instante sobre lo ya cargado (no necesita "Aplicar" ni
  // volver a pedirle datos al backend).
  const [typeFilter, setTypeFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.accounting.overtimeExtras.noveltiesList({
        location,
        from: applied.from,
        to: applied.to,
        limit: 3000,
        order: 'asc',
      });
      const data = Array.isArray(res?.data) ? res.data : [];
      // Una novedad rechazada, cancelada o en borrador no ocurrió: no debe
      // contar ni como volumen ni como ausentismo.
      setRows(data.filter((r) => !['Rechazada', 'Cancelada', 'Borrador'].includes(String(r?.status ?? ''))));
    } catch (e) {
      setRows([]);
      setError(e?.message || 'No fue posible cargar los indicadores.');
    } finally {
      setLoading(false);
    }
  }, [location, applied.from, applied.to]);

  useEffect(() => { load(); }, [load]);

  // Tipos disponibles para el filtro — solo los que realmente aparecen en
  // el periodo cargado (mismo criterio de "Otro" que usa byType más abajo).
  const availableTypes = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { set.add(String(r.novelty_type || '').trim() || 'Otro'); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  // Si el tipo elegido deja de existir en el periodo (ej. se cambió el
  // rango de fechas), se vuelve a "Todos" en vez de dejar el filtro
  // mostrando gráficos vacíos sin explicación.
  useEffect(() => {
    if (typeFilter && !availableTypes.includes(typeFilter)) setTypeFilter('');
  }, [availableTypes, typeFilter]);

  const filteredRows = useMemo(() => {
    if (!typeFilter) return rows;
    return rows.filter((r) => (String(r.novelty_type || '').trim() || 'Otro') === typeFilter);
  }, [rows, typeFilter]);

  const monthly = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((r) => {
      const key = String(r.novelty_date || '').slice(0, 7);
      if (!key) return;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, count]) => ({ key, label: monthLabel(key), count }));
  }, [filteredRows]);

  const byType = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((r) => {
      const t = String(r.novelty_type || '').trim() || 'Otro';
      map.set(t, (map.get(t) || 0) + 1);
    });
    const sorted = Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, 6);
    const restTotal = sorted.slice(6).reduce((acc, [, c]) => acc + c, 0);
    const result = top.map(([type, count]) => ({ type, count, color: TYPE_COLORS[type] || OTHER_COLOR }));
    if (restTotal > 0) result.push({ type: 'Otros', count: restTotal, color: OTHER_COLOR });
    return result;
  }, [filteredRows]);

  const absenteeismRows = useMemo(
    () => filteredRows.filter((r) => ABSENTEEISM_TYPES.has(String(r.novelty_type || '').trim())),
    [filteredRows]
  );
  const permisosCount = useMemo(
    () => filteredRows.filter((r) => String(r.novelty_type || '').trim() === 'Permiso').length,
    [filteredRows]
  );
  const incapacidadesCount = useMemo(
    () => filteredRows.filter((r) => String(r.novelty_type || '').trim() === 'Incapacidad').length,
    [filteredRows]
  );
  const absenteeismTotalHours = useMemo(
    () => absenteeismRows.reduce((acc, r) => acc + (Number(r.hours_decimal) || 0), 0),
    [absenteeismRows]
  );
  const absenteeismMonthly = useMemo(() => {
    const map = new Map();
    absenteeismRows.forEach((r) => {
      const key = String(r.novelty_date || '').slice(0, 7);
      if (!key) return;
      map.set(key, (map.get(key) || 0) + (Number(r.hours_decimal) || 0));
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, hours]) => ({ key, label: monthLabel(key), hours: Math.round(hours * 10) / 10 }));
  }, [absenteeismRows]);

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredRows.map((r) => ({
      fecha: r.novelty_date || '',
      colaborador: r.collaborator_name || '',
      documento: r.collaborator_dni || '',
      tipo: r.novelty_type || '',
      horas: r.hours_decimal ?? 0,
      estado: r.status || '',
      jefe_inmediato: r.immediate_boss_name || '',
      motivo: r.reason || '',
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Indicadores RH');
    const typeSuffix = typeFilter ? `_${typeFilter.replace(/\s+/g, '-')}` : '';
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'recursos-humanos', reportName: 'Indicadores RRHH' });
    XLSX.writeFile(wb, `indicadores_rh_${applied.from}_${applied.to}${typeSuffix}.xlsx`);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold text-gray-900">Indicadores del área</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Novedades (permisos, incapacidades, licencias, etc.) del periodo seleccionado.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[11px] text-gray-500 uppercase tracking-wide mb-1">Desde</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              className="border rounded-lg px-3 py-2 text-sm bg-white"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 uppercase tracking-wide mb-1">Hasta</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="border rounded-lg px-3 py-2 text-sm bg-white"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 uppercase tracking-wide mb-1">Tipo de novedad</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">Todos</option>
              {availableTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => setApplied({ ...filters })}
            disabled={loading}
            className="px-3 py-2 rounded-lg text-sm text-white disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg, #7C3AED 0%, #A855F7 100%)' }}
          >
            Aplicar
          </button>
          <button
            type="button"
            onClick={exportExcel}
            disabled={loading || filteredRows.length === 0}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Exportar Excel
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Novedades del periodo</div>
          <div className="text-2xl font-semibold mt-1">{loading ? '...' : filteredRows.length}</div>
          <div className="text-xs text-gray-600 mt-1">{typeFilter ? `solo "${typeFilter}", sin rechazadas` : 'todas las clases, sin rechazadas'}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Permisos</div>
          <div className="text-2xl font-semibold mt-1" style={{ color: PALETTE.blue }}>{loading ? '...' : permisosCount}</div>
          <div className="text-xs text-gray-600 mt-1">cantidad realizados</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Incapacidades</div>
          <div className="text-2xl font-semibold mt-1" style={{ color: PALETTE.orange }}>{loading ? '...' : incapacidadesCount}</div>
          <div className="text-xs text-gray-600 mt-1">cantidad registradas</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Ausentismo</div>
          <div className="text-2xl font-semibold mt-1" style={{ color: PALETTE.red }}>{loading ? '...' : fmtHours(absenteeismTotalHours)} h</div>
          <div className="text-xs text-gray-600 mt-1">horas perdidas: permisos + incapacidades</div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-800 mb-3">Novedades por mes</div>
          {monthly.length === 0 ? (
            <div className="text-sm text-gray-500 py-10 text-center">{loading ? 'Cargando...' : 'Sin registros en el periodo.'}</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthly} margin={chartMargin}>
                <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e1e0d9" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(42,120,214,0.06)' }} />
                <Bar dataKey="count" name="Novedades" fill={PALETTE.blue} radius={[4, 4, 0, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-800 mb-3">Tipos de novedad</div>
          {byType.length === 0 ? (
            <div className="text-sm text-gray-500 py-10 text-center">{loading ? 'Cargando...' : 'Sin registros en el periodo.'}</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byType} margin={chartMargin}>
                <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e1e0d9" />
                <XAxis dataKey="type" tick={{ fontSize: 10, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} interval={0} angle={-20} textAnchor="end" height={56} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar dataKey="count" name="Cantidad" radius={[4, 4, 0, 0]} maxBarSize={24}>
                  {byType.map((entry) => (
                    <Cell key={entry.type} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4 xl:col-span-2">
          <div className="text-sm font-semibold text-gray-800 mb-1">Ausentismo por mes</div>
          <p className="text-xs text-gray-500 mb-3">Horas perdidas por permisos e incapacidades, mes a mes.</p>
          {absenteeismMonthly.length === 0 ? (
            <div className="text-sm text-gray-500 py-10 text-center">{loading ? 'Cargando...' : 'Sin permisos ni incapacidades en el periodo.'}</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={absenteeismMonthly} margin={chartMargin}>
                <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e1e0d9" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={32} />
                <Tooltip content={<CustomTooltip suffix=" h" />} cursor={{ fill: 'rgba(235,104,52,0.06)' }} />
                <Bar dataKey="hours" name="Horas" fill={PALETTE.orange} radius={[4, 4, 0, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
